// Messages exchanged over the room WebSocket, shared by client and server.

import type { Action, GameEvent, GameView, RulesConfig } from '../engine';

export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 5;
export const MAX_NAME_LENGTH = 20;
export const MAX_SEATS = 8;
export const MIN_SEATS_TO_START = 2;
export const MAX_MESSAGE_BYTES = 8 * 1024;
export const HISTORY_EVENTS = 300;
export const PING = 'ping';
export const PONG = 'pong';
export const BOT_NAMES = ['Robo', 'Chip', 'Gizmo', 'Sprocket', 'Widget', 'Bolt', 'Cog', 'Dot'];

export function isRoomCode(code: string): boolean {
  if (code.length !== ROOM_CODE_LENGTH) return false;
  for (const ch of code) if (!ROOM_CODE_ALPHABET.includes(ch)) return false;
  return true;
}

/** Trims, strips control characters, and caps length. Empty means invalid. */
export function cleanName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, MAX_NAME_LENGTH);
}

/** Validates host-supplied house rules; returns an error message or null. */
export function rulesError(r: unknown): string | null {
  if (typeof r !== 'object' || r === null || Array.isArray(r)) return 'invalid rules';
  const x = r as Record<string, unknown>;
  const int = (v: unknown, lo: number, hi: number) => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
  if (x.targetScore !== undefined && !int(x.targetScore, 10, 1000)) return 'target score must be 10–1000';
  if (x.minBid !== undefined && !int(x.minBid, 1, 12)) return 'minimum bid must be 1–12';
  if (x.kittySize !== undefined && x.kittySize !== 'auto' && !int(x.kittySize, 4, 6)) return 'kitty must be auto or 4–6';
  if (x.partnerWithThree !== undefined && typeof x.partnerWithThree !== 'boolean') return 'invalid partner option';
  // A rematch sends back the finished game's rules, which include the (fixed) limit.
  if (x.handLimit !== undefined && x.handLimit !== 6) return 'the hand limit is fixed at 6';
  return null;
}

export type ClientMessage =
  | { type: 'hello'; token?: string }
  | { type: 'join'; name: string }
  | { type: 'leave' }
  /** Host only, in the lobby. */
  | { type: 'addBot' }
  | { type: 'removeBot'; seat: number }
  /** Host only: start (or restart after a game ends) with the seated players. */
  | { type: 'start'; rules?: Partial<RulesConfig> }
  | { type: 'action'; action: Action }
  /** Host only: take back the most recent person's move (and the computer moves after it). */
  | { type: 'undo' }
  /** Host only, for an away person's seat: open it for someone to claim, or hand it to a computer. */
  | { type: 'release'; seat: number; toBot?: boolean }
  /** A visitor takes over an open seat or a computer's seat. */
  | { type: 'claim'; seat: number };

export type RoomStatus = 'lobby' | 'playing' | 'finished';

export interface SeatSummary {
  name: string;
  connected: boolean;
  open: boolean;
  bot: boolean;
}

export interface RoomSnapshot {
  code: string;
  status: RoomStatus;
  seats: SeatSummary[];
  /** The lowest-numbered connected person. */
  host: number;
  spectators: number;
  rules: RulesConfig | null;
}

export type ServerMessage =
  | { type: 'welcome'; seat: number | null; token?: string }
  | { type: 'room'; room: RoomSnapshot }
  | { type: 'game'; view: GameView; legal: Action[]; events: GameEvent[]; reset: boolean }
  | { type: 'info'; message: string }
  | { type: 'error'; message: string; fatal?: boolean };
